import * as THREE from 'three';
import { EffectComposer } from './basler-components.es411.js';
import { UnrealBloomPass } from './basler-components.es412.js';
import { RenderPass } from './basler-components.es413.js';
import { ShaderPass } from './basler-components.es414.js';
import { verticalBlurShader } from './basler-components.es419.js';
import planeFragment from './basler-components.es420.js';
import planeVertex from './basler-components.es421.js';
import { supported } from './basler-components.es418.js';

class HeroCanvasRenderer {
  progress = 1;
  options;
  scene;
  camera;
  renderer = null;
  timing = {
    interval: 1 / 9,
    // max 24 FPS
    clock: new THREE.Clock(),
    delta: 0
  };
  webglCanvas = null;
  shaderMaterial = null;
  planeGeometry = null;
  planeMesh = null;
  composer = null;
  guiParams;
  paused = true;
  supportsHardwareGL = false;
  static isWebGLSupported() {
    try {
      const canvas = document.createElement("canvas");
      return !!(window.WebGLRenderingContext && (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")));
    } catch (e) {
      return false;
    }
  }
  /**
   *
   * @param options HeroCanvasRendererOptions
   */
  constructor(options) {
    this.progress = options.progress;
    this.options = options;
    this.webglCanvas = null;
    this.renderer = null;
    this.shaderMaterial = null;
    this.planeGeometry = null;
    this.planeMesh = null;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 9e3);
    this.camera.position.z = 1;
    this.paused = true;
    this.guiParams = {
      speed: 0.055 / 4,
      color1: options.color1,
      color2: options.color2,
      fov: 45,
      transparency: 0.75,
      progress: 0.95
    };
    this.supportsHardwareGL = supported({
      failIfMajorPerformanceCaveat: true
    });
    this.renderLoop = this.renderLoop.bind(this);
    this.onWebGLContextLost = this.onWebGLContextLost.bind(this);
    this.onWebGLContextRestored = this.onWebGLContextRestored.bind(this);
  }
  /**
   * Initializes the renderer, scene, and camera.
   * If WebGL is not supported, it logs an error and exits.
   */
  init(webglCanvas) {
    if (!HeroCanvasRenderer.isWebGLSupported()) {
      console.error("HeroCanvasRenderer - WebGL is not supported in this browser.");
      return;
    }
    this.webglCanvas = webglCanvas;
    const container = this.webglCanvas.parentElement;
    if (!container) {
      return;
    }
    this.webglCanvas.addEventListener("webglcontextlost", this.onWebGLContextLost, false);
    this.webglCanvas.addEventListener("webglcontextrestored", this.onWebGLContextRestored, false);
    const box = container.getBoundingClientRect();
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      canvas: this.webglCanvas,
      // precision: 'mediump'
      depth: true
    });
    this.renderer.setClearColor(0, 0);
    this.renderer.setPixelRatio(window.devicePixelRatio || 1);
    if (box && box.width > 1024) {
      this.camera.aspect = box.width / box.height;
      this.camera.updateProjectionMatrix();
    }
    this.camera.near = 0.1;
    this.camera.far = 1e3;
    this.camera.position.z = 150;
    this.camera.matrix.fromArray([
      -0.5718335520439226,
      -11102230246251565e-32,
      0.8203696659170365,
      0,
      0.591723650400024,
      0.6926341876613774,
      0.4124572749266682,
      0,
      -0.5682160771344822,
      0.7212890419815505,
      -0.3960714677974625,
      0,
      -812.4643774173694,
      400.13237655287,
      -159.3707320694093,
      1
    ]);
    this.camera.matrix.decompose(this.camera.position, this.camera.quaternion, this.camera.scale);
    try {
      if (/iPad|iPhone|iPod/.test(navigator?.userAgent)) {
      } else {
        this.renderer.setSize(this.webglCanvas.clientWidth, this.webglCanvas.clientHeight, false);
      }
    } catch (e) {
      console.error(e);
    }
    this.initMeshes();
    this.initScene();
  }
  /**
   * Handles the WebGL context lost event.
   * Pauses the render loop and prevents the default behavior.
   */
  onWebGLContextLost(event) {
    event.preventDefault();
    console.warn("→ WebGL context lost", event);
    this.paused = true;
  }
  /**
   * Handles the WebGL context restored event.
   * Reinitializes meshes and scene, then resumes the render loop.
   */
  onWebGLContextRestored(event) {
    console.info("→ WebGL context restored", event);
    this.initMeshes();
    this.initScene();
    this.setPaused(false);
  }
  /**
   * Starts or pauses the render loop.
   */
  setPaused(newStatus) {
    this.paused = newStatus;
    if (!this.paused) {
      this.timing.clock.start();
      this.renderLoop();
    } else {
      this.timing.clock.stop();
    }
  }
  initMeshes() {
    this.planeGeometry = new THREE.PlaneGeometry(-280.65, -132.02, 200, 200);
    this.shaderMaterial = new THREE.ShaderMaterial({
      // extensions: {
      //   derivatives: '#extension GL_OES_standard_derivatives : enable'
      // },
      uniforms: {
        time: { type: "f", value: 115 },
        colorScheme: { type: "f", value: 2 },
        transparency: {
          type: "f",
          value: this.guiParams.transparency
        },
        progress: { type: "f", value: this.guiParams.guiProgress },
        color1: {
          type: "v3",
          value: new THREE.Color(this.guiParams.color1)
        },
        color2: {
          type: "v3",
          value: new THREE.Color(this.guiParams.color2)
        }
        // mousePos: { type: 'v2', value: new THREE.Vector2(0, 0) } as IUniform
      },
      vertexShader: planeVertex,
      fragmentShader: planeFragment,
      // wireframe: true,
      side: THREE.DoubleSide,
      transparent: true
    });
    this.planeMesh = new THREE.Mesh(this.planeGeometry, this.shaderMaterial);
    this.planeMesh.scale.x *= 2.6;
    this.planeMesh.position.x -= 40;
    this.planeMesh.position.y -= 40;
    this.planeMesh.position.z -= 50;
    this.planeMesh.rotation.x = -Math.PI / 2;
  }
  setProgress(progress) {
    if (progress < -1) {
      progress = -1;
    }
    if (progress > 1.5) {
      progress = 1.5;
    }
    this.progress = progress;
  }
  setColor1(color) {
    this.guiParams.color1 = color;
    this.shaderMaterial?.uniforms.color1.value.set(color);
  }
  setColor2(color) {
    this.guiParams.color2 = color;
    this.shaderMaterial?.uniforms.color2.value.set(color);
  }
  initScene() {
    if (!this.renderer || !this.webglCanvas) return;
    this.scene.clear();
    if (this.planeMesh) {
      this.scene.add(this.planeMesh);
    }
    if (this.options.showDebugControls) {
      const gridHelper = new THREE.GridHelper(100, 50);
      this.scene.add(gridHelper);
    }
    if (this.options.usePostProcessing) {
      this.composer = new EffectComposer(this.renderer);
      this.renderer.setSize(this.webglCanvas.clientWidth, this.webglCanvas.clientHeight);
      this.composer.addPass(new RenderPass(this.scene, this.camera));
      if (this.options.useUnrealBloom) {
        this.composer.addPass(
          new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.8, 2.5, 0.21)
        );
      } else {
        const pass = new ShaderPass(verticalBlurShader);
        pass.uniforms["v"].value = 1 / this.webglCanvas.clientHeight;
        this.composer.addPass(pass);
      }
    }
  }
  onResize() {
    if (!this.webglCanvas || !this.renderer) return;
    const container = this.webglCanvas.parentElement;
    if (container) {
      const box = container.getBoundingClientRect();
      if (this.planeMesh && box.width < 1024) {
        this.planeMesh.scale.x = 2.6;
        this.planeMesh.scale.y = 1;
        this.planeMesh.scale.z = 1;
      } else {
        this.camera.aspect = box.width / box.height;
        this.camera.updateProjectionMatrix();
      }
      this.renderer.setSize(box.width, box.height);
      if (this.planeMesh) ;
    }
  }
  renderLoop() {
    if (this.paused) return;
    if (this.supportsHardwareGL) {
      requestAnimationFrame(this.renderLoop);
    }
    this.timing.delta += this.timing.clock.getDelta();
    if (this.timing.delta > this.timing.interval) {
      if (this.shaderMaterial) {
        this.shaderMaterial.uniforms.time.value += 0.15;
        this.shaderMaterial.uniforms.progress.value = this.progress;
      }
      this.timing.delta = this.timing.delta % this.timing.interval;
      this.renderOnce();
    }
  }
  renderOnce() {
    if (this.renderer) {
      this.camera.updateProjectionMatrix();
      this.renderer.resetState();
      if (this.options.usePostProcessing && this.composer) {
        this.composer.render();
      } else {
        this.renderer.render(this.scene, this.camera);
      }
    }
  }
  dispose() {
    this.paused = true;
    if (this.webglCanvas) {
      this.webglCanvas.removeEventListener("webglcontextlost", this.onWebGLContextLost);
      this.webglCanvas.removeEventListener("webglcontextrestored", this.onWebGLContextRestored);
    }
    this.renderer?.forceContextLoss();
    this.renderer?.dispose();
    this.planeGeometry?.dispose();
    this.shaderMaterial?.dispose();
    this.composer?.dispose();
    this.webglCanvas = null;
    this.renderer = null;
    this.shaderMaterial = null;
    this.planeGeometry = null;
    this.planeMesh = null;
    this.composer = null;
  }
}

export { HeroCanvasRenderer };
